<script setup>
import { ref, onMounted } from "vue";

const props = defineProps({
  label: {
    type: String,
    required: true,
  },
  errors: {
    type: Array,
    default: () => [],
  },
  type: {
    type: String,
    default: "text",
  },
  id: {
    type: String,
    default: null,
  },
  modelValue: {
    type: String,
    default: "",
  },
});

const emit = defineEmits(["update:modelValue"]);

const input = ref(null);
const showPassword = ref(false);

onMounted(() => {
  if (input.value && input.value.hasAttribute("autofocus")) {
    input.value.focus();
  }
});

function handleInput(event) {
  emit("update:modelValue", event.target.value);
}

defineExpose({ focus: () => input.value?.focus() });
</script>

<template>
  <label
    :for="id"
    class="first-letter:uppercase block mb-2 font-medium text-gray-900 dark:text-white"
  v-html="label">
  
  </label>
  <textarea
    v-if="type === 'textarea'"
    :id="id"
    v-bind="$attrs"
    :class="[
      'bg-gray-50 border border-gray-300 text-gray-900 rounded-lg focus:ring-main focus:border-main block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-main dark:focus:border-main',
      { 'border-red-500': errors.length > 0 },
    ]"
    :value="modelValue"
    @input="handleInput"
    ref="input"
  ></textarea>
  <div v-else-if="type === 'password' || id === 'password' || id === 'password_confirmation'" class="relative">
    <input
      :type="showPassword ? 'text' : 'password'"
      :id="id"
      v-bind="$attrs"
      :class="[
        'bg-gray-50 border border-gray-300 text-gray-900 rounded-lg focus:ring-main focus:border-main block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-main dark:focus:border-main',
        { 'border-red-500': errors.length > 0 },
      ]"
      :value="modelValue"
      @input="handleInput"
      ref="input"
    />
    <div
      @click="showPassword = !showPassword"
      class="absolute w-14 h-full top-0 right-0 flex justify-center items-center"
    >
      <svg
        v-if="showPassword"
        class="w-6 h-6 fill-gray-500"
        xmlns="http://www.w3.org/2000/svg"
        xmlns:xlink="http://www.w3.org/1999/xlink"
        version="1.1"
        id="Capa_1"
        x="0px"
        y="0px"
        viewBox="0 0 488.85 488.85"
        style="enable-background: new 0 0 488.85 488.85"
        xml:space="preserve"
      >
        <g>
          <path
            d="M244.425,98.725c-93.4,0-178.1,51.1-240.6,134.1c-5.1,6.8-5.1,16.3,0,23.1c62.5,83.1,147.2,134.2,240.6,134.2   s178.1-51.1,240.6-134.1c5.1-6.8,5.1-16.3,0-23.1C422.525,149.825,337.825,98.725,244.425,98.725z M251.125,347.025   c-62,3.9-113.2-47.2-109.3-109.3c3.2-51.2,44.7-92.7,95.9-95.9c62-3.9,113.2,47.2,109.3,109.3   C343.725,302.225,302.225,343.725,251.125,347.025z M248.025,299.625c-33.4,2.1-61-25.4-58.8-58.8c1.7-27.6,24.1-49.9,51.7-51.7   c33.4-2.1,61,25.4,58.8,58.8C297.925,275.625,275.525,297.925,248.025,299.625z"
          />
        </g>
      </svg>
      <svg
        v-else
        class="w-6 h-6 fill-gray-500"
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 512 512"
      >
        <g id="Layer_16" data-name="Layer 16">
          <path
            d="m419.72 419.72-327.46-327.45-.07-.08a19 19 0 0 0 -26.78 27l28.67 28.67a332.64 332.64 0 0 0 -88.19 89 34.22 34.22 0 0 0 0 38.38c59.97 88.76 155.04 140.76 250.11 140.11a271.6 271.6 0 0 0 90.46-15.16l46.41 46.41a19 19 0 0 0 26.94-26.79zm-163.72-55.98a107.78 107.78 0 0 1 -98.17-152.18l29.95 29.95a69.75 69.75 0 0 0 82.71 82.71l29.95 29.95a107.23 107.23 0 0 1 -44.44 9.57z"
          />
          <path
            d="m506.11 236.81c-59.97-88.81-155.04-140.81-250.11-140.16a271.6 271.6 0 0 0 -90.46 15.16l46 46a107.78 107.78 0 0 1 142.63 142.63l63.74 63.74a332.49 332.49 0 0 0 88.2-89 34.22 34.22 0 0 0 0-38.37z"
          />
          <path
            d="m256 186.26a69.91 69.91 0 0 0 -14.49 1.52l82.71 82.7a69.74 69.74 0 0 0 -68.22-84.22z"
          />
        </g>
      </svg>
    </div>
  </div>
  <input
    v-else
    :type="type"
    :id="id"
    v-bind="$attrs"
    :class="[
      'bg-gray-50 border border-gray-300 text-gray-900 rounded-lg focus:ring-main focus:border-main block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-main dark:focus:border-main',
      { 'border-red-500': errors.length > 0 },
    ]"
    :value="modelValue"
    @input="handleInput"
    ref="input"
  />

  <p v-if="errors && errors[0]" class="text-red-600 mt-1">
    {{ errors[0] }}
  </p>
</template>
